import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
   Button,
   Drawer,
   Empty,
   Input,
   message,
   Modal,
   Popconfirm,
   Skeleton,
   Space,
   Spin,
   Table,
   Tabs,
   Tooltip,
   Typography,
} from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { 
   EditOutlined,
   UsergroupAddOutlined, 
   DeleteOutlined,
   SolutionOutlined,
   SearchOutlined,
   UnorderedListOutlined,
   ClockCircleOutlined,
   HistoryOutlined,
   BookOutlined,
   FileTextOutlined,
   PlusOutlined,
   CloseOutlined,
   EyeOutlined,
   CopyOutlined,
} from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import SimpleBarReact from 'simplebar-react';
import moment from 'moment';
import ResponsiveBreadcrumb from '../../../components/breadcrumb/ResponsiveBreadcrumb';
import Utils from '../../../misc/Utils';
import Enums from '../../../misc/Enums';
import PerformanceTaskForm from './PerformanceTaskForm';
import PerformanceTaskAssignTool from './PerformanceTaskAssignTool';
import PerformanceTaskSubmissions from './PerformanceTaskSubmissions';
import {
   createPerformanceTask,
   createPerformanceTaskAssignment,
   deletePerformanceTask,
   duplicatePerformanceTask,
   getPerformanceTaskAssignment,
   listDuplicatedPerformanceTasks,
   listPerformanceTaskAssignments,
   listPerformanceTasks,
   updatePerformanceTask,
   updatePerformanceTaskAssignment,
} from '../../../services/MicrocredentialsPerformanceTaskService';
import '../../../pages/common/BraineeModule.css';
import {
   getMicrocredentialsTaskModule,
   MICROCREDENTIALS_TASK_MODES,
} from '../../../config/microcredentialsTaskModules';
import { isPlagiarismCheckerEnabled } from '../../HomeworksPage/plagiarismUtils';

const { Text } = Typography;

const ALLOWED_ROLES = ['super admin', 'admin', 'teacher', 'co-teacher'];
const SUPER_ADMIN_ROLE = 'super admin';
const TAB_KEYS = {
   MASTER: 'master',
   MY_PERFORMANCE_TASK: 'my-performance-task',
   CURRENT: 'current',
   UPCOMING: 'upcoming',
   PAST: 'past',
};

function formatDisplayDate(value) {
   if (!value) return '-';
   const parsed = moment(value);
   return parsed.isValid() ? parsed.format('MMM D, YYYY h:mm a') : value;
}

function getTaskMcptId(record) {
   return record?.mcpt_id ?? record?.Mcpt_id ?? record?.performance_task?.mcpt_id ?? record?.performanceTask?.mcpt_id ?? record?.task?.mcpt_id ?? null;
}

function getTaskRecordId(record) {
   return record?.id ?? record?.duplicate_id ?? record?.mcpt_duplicate_id ?? getTaskMcptId(record) ?? null;
}

function getAssignmentId(record) {
   return record?.mcpta_id ?? record?.id ?? record?.assignment_id ?? record?.assignmentId ?? record?.performance_task_assignment_id ?? null;
}

function getTaskTitle(record) {
   return record?.title ?? record?.Title ?? record?.performance_task_title ?? record?.performanceTaskTitle ?? record?.performance_task?.title ?? record?.performanceTask?.title ?? record?.task?.title ?? 'Performance Task';
}

function getTaskCourseId(record) {
   return record?.course_id ?? record?.Course_id ?? record?.courseId ?? record?.course?.course_id ?? record?.course?.id ?? record?.performance_task?.course_id ?? record?.performance_task?.Course_id ?? record?.performance_task?.course?.course_id ?? record?.performance_task?.course?.id ?? record?.performanceTask?.course_id ?? record?.performanceTask?.course?.course_id ?? record?.task?.course_id ?? record?.task?.course?.course_id ?? null;
}

function getTaskCourseName(record) {
   return record?.course_name ?? record?.courseName ?? record?.course?.name ?? record?.course?.Name ?? record?.performance_task?.course_name ?? record?.performance_task?.course?.name ?? record?.performance_task?.course?.Name ?? record?.performanceTask?.course_name ?? record?.performanceTask?.course?.name ?? record?.task?.course_name ?? record?.task?.course?.name ?? '';
}

function getTaskTopicId(record) {
   return record?.topic_id ?? record?.Topic_id ?? record?.topicId ?? record?.topic?.topic_id ?? record?.topic?.id ?? record?.performance_task?.topic_id ?? record?.performance_task?.Topic_id ?? record?.performance_task?.topic?.topic_id ?? record?.performanceTask?.topic_id ?? record?.performanceTask?.topic?.topic_id ?? record?.task?.topic_id ?? record?.task?.topic?.topic_id ?? null;
}

function getTaskTopicTitle(record) {
   return record?.topic_title ?? record?.topicTitle ?? record?.topic?.title ?? record?.topic?.Title ?? record?.performance_task?.topic_title ?? record?.performance_task?.topic?.title ?? record?.performance_task?.topic?.Title ?? record?.performanceTask?.topic_title ?? record?.performanceTask?.topic?.title ?? record?.task?.topic_title ?? record?.task?.topic?.title ?? '';
}

function getTaskDescription(record) {
   return record?.description ?? record?.Description ?? record?.performance_task?.description ?? record?.performance_task?.Description ?? record?.performanceTask?.description ?? record?.task?.description ?? '';
}

function getTaskMaxPoints(record) {
   return record?.max_points ?? record?.maxPoints ?? record?.Max_points ?? record?.performance_task?.max_points ?? record?.performance_task?.Max_points ?? record?.performanceTask?.max_points ?? record?.task?.max_points ?? undefined;
}

function normalizePerformanceTaskRecord(record, index, prefix = 'task') {
   return {
      ...record,
      title: getTaskTitle(record),
      description: getTaskDescription(record),
      course_id: getTaskCourseId(record),
      course_name: getTaskCourseName(record),
      topic_id: getTaskTopicId(record),
      topic_title: getTaskTopicTitle(record),
      max_points: getTaskMaxPoints(record),
      plagiarism_checker: isPlagiarismCheckerEnabled(record?.plagiarism_checker ?? record?.plagiarismChecker ?? record?.Plagiarism_checker),
      document: record?.document ?? record?.Document ?? record?.performance_task?.document ?? record?.performanceTask?.document ?? record?.task?.document ?? '',
      created_by: record?.created_by ?? record?.createdBy ?? record?.performance_task?.created_by ?? record?.performanceTask?.created_by ?? record?.task?.created_by ?? null,
      key: getTaskRecordId(record) || `${prefix}-${index}`,
   };
}

function normalizeAssignmentRecord(record, temporal, index) {
   const title =
      record?.assignment_title ??
      record?.performance_task_title ??
      record?.performanceTaskTitle ??
      record?.title ??
      record?.performance_task?.title ??
      'Untitled Performance Task';
   const sections = record?.assigned_to_sections ?? record?.sections ?? record?.section_desc ?? '';
   const assignedToLabel = Array.isArray(sections)
      ? sections.join(', ')
      : String(sections || '').replace(/<br\s*\/?>/gi, ', ');

   return {
      ...record,
      key: getAssignmentId(record) || `${temporal}-${index}`,
      assignment_title: title,
      // level_label: record?.level_desc ?? record?.level_name ?? record?.level ?? '-',
      level_label: record?.level_desc ?? record?.level_name ?? '-',
      assigned_by_label: record?.assigned_by_name ?? record?.assigned_by_fullname ?? record?.assignedByName ?? record?.assigned_by ?? '-',
      assigned_to_label: assignedToLabel || '-',
      submission_date_label: record?.submission_date ?? record?.submit_date ?? record?.due_date ?? record?.end_date ?? '',
      date_assigned_label: record?.date_created ?? record?.created_at ?? record?.createdAt ?? '',
      task_id: record?.mcpt_id ?? record?.performance_task_id ?? record?.performanceTaskId ?? record?.performance_task?.mcpt_id ?? record?.performance_task?.id ?? null,
      level_value: record?.level ?? record?.level_id ?? record?.levelId ?? null,
      course_id: record?.course_id ?? record?.courseId ?? record?.course?.course_id ?? record?.course?.id ?? null,
      plagiarism_checker: isPlagiarismCheckerEnabled(record?.plagiarism_checker ?? record?.plagiarismChecker ?? record?.Plagiarism_checker),
   };
}

function toAssignmentOptions(record, fallbackTitle = '') {
   const assignmentId = getAssignmentId(record) ?? '';
   const performanceTaskId =
      record?.mcpt_id ??
      record?.Mcpt_id ??
      record?.performance_task?.mcpt_id ??
      record?.performanceTask?.mcpt_id ??
      (typeof record?.performance_task_id === 'string' && record.performance_task_id.startsWith('mcpt-') ? record.performance_task_id : null) ??
      (typeof record?.performanceTaskId === 'string' && record.performanceTaskId.startsWith('mcpt-') ? record.performanceTaskId : null) ??
      null;

   return {
      id: assignmentId,
      mcpta_id: assignmentId,
      assignment_id: assignmentId,
      performance_task_id: performanceTaskId,
      performanceTaskId: performanceTaskId,
      mcpt_id: performanceTaskId,
      course_id: record?.course_id ?? record?.courseId ?? record?.course?.course_id ?? record?.course?.id ?? null,
      assigned_by: record?.assigned_by ?? record?.assignedBy ?? null,
      level: record?.level ?? record?.level_id ?? '',
      submission_date: record?.submission_date ?? record?.submit_date ?? record?.due_date ?? record?.end_date ?? '',
      assigned_students: record?.assigned_students ?? record?.assignedStudents ?? '',
      title: fallbackTitle || getTaskTitle(record),
   };
}

export function MicrocredentialsPerformanceTasks({ mode = MICROCREDENTIALS_TASK_MODES.PERFORMANCE } = {}) {
   const navigate = useNavigate();

   const search = window.location.search;
   const query = new URLSearchParams(search);

   const courseId = query.get('id');
   const courseKey = query.get('courseKey') || courseId;
   const courseName = query.get('name');
   //const userId = query.get('staff') ?? localStorage.getItem('selected-teacher') ?? Utils.getCurrentUserInfo(Enums.UserInfo.id);
   const userId = Utils.getCurrentUserInfo(Enums.UserInfo.id);
   const userRole = Utils.getCurrentUserInfo(Enums.UserInfo.role);
   const moduleConfig = getMicrocredentialsTaskModule(mode);
   const taskLabel = moduleConfig.label;
   const taskLabelLower = taskLabel.toLowerCase();
   const taskLabelPlural = moduleConfig.labelPlural;
   const myTaskLabel = moduleConfig.myLabel;
   const myTaskLabelPlural = moduleConfig.myLabelPlural;

   const [searchText, setSearchText] = useState('');
   const [searchedColumn, setSearchedColumn] = useState('');
   const [activeTab, setActiveTab] = useState(TAB_KEYS.MASTER);
   const [isCompactListView, setIsCompactListView] = useState(window.innerWidth <= 768);

   const [loadingTasks, setLoadingTasks] = useState(false);
   const [loadingDuplicatedTasks, setLoadingDuplicatedTasks] = useState(false);
   const [loadingAssignments, setLoadingAssignments] = useState({
      [TAB_KEYS.CURRENT]: false,
      [TAB_KEYS.UPCOMING]: false,
      [TAB_KEYS.PAST]: false,
   });
   const [saving, setSaving] = useState(false);
   const [assignmentSaving, setAssignmentSaving] = useState(false);
   const [assignmentPanelLoading, setAssignmentPanelLoading] = useState(false);

   const [tasks, setTasks] = useState([]);
   const [duplicatedTasks, setDuplicatedTasks] = useState([]);
   const [currentAssignments, setCurrentAssignments] = useState([]);
   const [upcomingAssignments, setUpcomingAssignments] = useState([]);
   const [pastAssignments, setPastAssignments] = useState([]);

   const [createOpen, setCreateOpen] = useState(false);
   const [editOpen, setEditOpen] = useState(false);
   const [editingRecord, setEditingRecord] = useState(null);
   const [previewOpen, setPreviewOpen] = useState(false);
   const [previewRecord, setPreviewRecord] = useState(null);

   const [assignmentDrawerOpen, setAssignmentDrawerOpen] = useState(false);
   const [assignmentOptions, setAssignmentOptions] = useState(null);
   const [assignmentTitle, setAssignmentTitle] = useState('');
   const [submissionsOpen, setSubmissionsOpen] = useState(false);
   const [submissionTaskId, setSubmissionTaskId] = useState(null);
   const [submissionLevelId, setSubmissionLevelId] = useState(null);
   const [submissionAssignmentId, setSubmissionAssignmentId] = useState(null);
   const [submissionAssignedBy, setSubmissionAssignedBy] = useState(null);
   const [submissionTitle, setSubmissionTitle] = useState('');
   const [submissionPlagiarismCheckerEnabled, setSubmissionPlagiarismCheckerEnabled] = useState(false);

   const isSuperAdmin = String(userRole || '').toLowerCase() === SUPER_ADMIN_ROLE;
   const isAllAssignmentsUser = ['super admin', 'admin'].includes(String(userRole || '').toLowerCase());
   const canEditPerformanceTask = isSuperAdmin || activeTab === TAB_KEYS.MY_PERFORMANCE_TASK;

   const getErrorMessage = useCallback((error, fallbackMessage) => {
      const responseData = error?.response?.data;

      if (typeof responseData === 'string' && responseData.trim()) {
         return responseData;
      }

      if (responseData?.errors) {
         const firstFieldEntry = Object.entries(responseData.errors).find(([, value]) => Array.isArray(value) && value.length > 0);
         if (firstFieldEntry) {
            const [fieldName, fieldErrors] = firstFieldEntry;
            return `${fieldName}: ${fieldErrors[0]}`;
         }
      }

      if (responseData?.title) {
         return responseData.title;
      }

      if (responseData?.message) {
         return responseData.message;
      }

      return fallbackMessage;
   }, []);

   const closeCreateModal = useCallback(() => {
      setCreateOpen(false);
   }, []);

   const closeEditModal = useCallback(() => {
      setEditOpen(false);
      setEditingRecord(null);
   }, []);

   const closePreviewModal = useCallback(() => {
      setPreviewOpen(false);
      setPreviewRecord(null);
   }, []);

   const closeAssignmentDrawer = useCallback(() => {
      setAssignmentDrawerOpen(false);
      setAssignmentPanelLoading(false);
      setAssignmentOptions(null);
      setAssignmentTitle('');
   }, []);

   const closeSubmissionsModal = useCallback(() => {
      setSubmissionsOpen(false);
      setSubmissionTaskId(null);
      setSubmissionLevelId(null);
      setSubmissionAssignmentId(null);
      setSubmissionAssignedBy(null);
      setSubmissionTitle('');
      setSubmissionPlagiarismCheckerEnabled(false);
   }, []);

   const loadTasks = useCallback(async () => {
      try {
         setLoadingTasks(true);
         const data = await listPerformanceTasks({
            created_by: userId,
            course_id: courseKey,
         }, mode);
         const normalized = Array.isArray(data) ? data : [];
         const filtered = courseKey
            ? normalized.filter((item) => String(getTaskCourseId(item)) === String(courseKey))
            : normalized;

         const mappedTasks = filtered.map((item, index) => normalizePerformanceTaskRecord(item, index, 'task'));

         setTasks(mappedTasks);
         return mappedTasks;
      } catch (error) {
         message.error(getErrorMessage(error, `Failed to load ${taskLabelLower}s`));
         return [];
      } finally {
         setLoadingTasks(false);
      }
   }, [courseKey, getErrorMessage, mode, userId]);

   const loadDuplicatedTasks = useCallback(async () => {
      try {
         setLoadingDuplicatedTasks(true);
         const data = await listDuplicatedPerformanceTasks({
            created_by: Number(userId),
            task_type: moduleConfig.taskType,
         }, mode);
         const normalized = Array.isArray(data) ? data : [];
         const filtered = courseKey
            ? normalized.filter((item) => String(getTaskCourseId(item)) === String(courseKey))
            : normalized;

         const mappedTasks = filtered.map((item, index) => normalizePerformanceTaskRecord(item, index, 'duplicated-task'));

         setDuplicatedTasks(mappedTasks);
         return mappedTasks;
      } catch (error) {
         message.error(getErrorMessage(error, `Failed to load duplicated ${taskLabelLower}s`));
         return [];
      } finally {
         setLoadingDuplicatedTasks(false);
      }
   }, [courseKey, getErrorMessage, mode, moduleConfig.taskType, userId]);

   const loadAssignments = useCallback(async (temporal, courseTaskIds = []) => {
      try {
         setLoadingAssignments((prev) => ({ ...prev, [temporal]: true }));
         const data = await listPerformanceTaskAssignments({
            assigned_by: userId,
            temporal,
            useAllAssigned: isAllAssignmentsUser,
         }, mode);

         const taskIdSet = new Set((courseTaskIds || []).filter(Boolean).map((id) => String(id)));
         const normalized = (Array.isArray(data) ? data : [])
            .map((item, index) => normalizeAssignmentRecord(item, temporal, index))
            .filter((item) => {
               const itemCourseId = item.course_id;
               if (courseKey && itemCourseId !== undefined && itemCourseId !== null && itemCourseId !== '') {
                  return String(itemCourseId) === String(courseKey);
               }

               if (taskIdSet.size > 0 && item.task_id) {
                  return taskIdSet.has(String(item.task_id));
               }

               return taskIdSet.size === 0 ? false : true;
            });

         switch (temporal) {
            case TAB_KEYS.CURRENT:
               setCurrentAssignments(normalized);
               break;
            case TAB_KEYS.UPCOMING:
               setUpcomingAssignments(normalized);
               break;
            case TAB_KEYS.PAST:
               setPastAssignments(normalized);
               break;
            default:
               break;
         }
      } catch (error) {
         message.error(getErrorMessage(error, `Failed to load ${temporal} assignments`));
      } finally {
         setLoadingAssignments((prev) => ({ ...prev, [temporal]: false }));
      }
   }, [courseKey, getErrorMessage, isAllAssignmentsUser, mode, userId]);

   const loadAssignmentsForTab = useCallback(async (tabKey = activeTab, courseTasks = tasks) => {
      if (![TAB_KEYS.CURRENT, TAB_KEYS.UPCOMING, TAB_KEYS.PAST].includes(tabKey)) {
         return;
      }

      const taskIds = (courseTasks || []).map((item) => getTaskMcptId(item)).filter(Boolean);
      await loadAssignments(tabKey, taskIds);
   }, [activeTab, loadAssignments, tasks]);

   useEffect(() => {
      Utils.verifyJwt();

      if (!ALLOWED_ROLES.includes(String(userRole || '').toLowerCase())) {
         navigate('/home');
         return;
      }

      if (!courseId) {
         navigate(moduleConfig.teacherListRoute);
         return;
      }

      loadTasks();
      loadDuplicatedTasks();
   }, [courseId, loadDuplicatedTasks, loadTasks, navigate, userRole]);

   useEffect(() => {
      const updateCompactListView = () => {
         setIsCompactListView(window.innerWidth <= 768);
      };

      updateCompactListView();
      window.addEventListener('resize', updateCompactListView);

      return () => {
         window.removeEventListener('resize', updateCompactListView);
      };
   }, []);

   useEffect(() => {
      loadAssignmentsForTab();
   }, [activeTab, loadAssignmentsForTab, tasks]);

   useEffect(() => {
      if (activeTab === TAB_KEYS.MY_PERFORMANCE_TASK) {
         loadDuplicatedTasks();
      }
   }, [activeTab, loadDuplicatedTasks]);

   useEffect(() => {
      if (tasks.length === 0) {
         setCurrentAssignments([]);
         setPastAssignments([]);
         return;
      }

      loadAssignments(TAB_KEYS.CURRENT, tasks.map((item) => getTaskMcptId(item)).filter(Boolean));
      loadAssignments(TAB_KEYS.PAST, tasks.map((item) => getTaskMcptId(item)).filter(Boolean));
   }, [loadAssignments, tasks]);

   const handleCreate = async (payload, filesForUpload) => {
      if (!isSuperAdmin) {
         message.error(`Only super admin can create ${taskLabelLower}s.`);
         return;
      }

      try {
         setSaving(true);
         const finalPayload = {
            ...payload,
            course_id: payload.course_id ?? courseKey ?? courseId,
            topic_id: payload.topic_id,
            created_by: Number(userId),
         };

         await createPerformanceTask(finalPayload, filesForUpload, mode);
         message.success(`${taskLabel} created`);
         closeCreateModal();
         await loadTasks();
      } catch (error) {
         message.error(getErrorMessage(error, `Failed to create ${taskLabelLower}`));
      } finally {
         setSaving(false);
      }
   };

   const handleEdit = async (payload, filesForUpload) => {
      if (!isSuperAdmin && editingRecord?.source_tab !== TAB_KEYS.MY_PERFORMANCE_TASK) {
         message.error(`Only super admin can edit ${taskLabelLower}s.`);
         return;
      }

      try {
         const mcptId = editingRecord?.mcpt_id ?? editingRecord?.Mcpt_id ?? editingRecord?.id;
         if (!mcptId) return;

         setSaving(true);

         const finalPayload = {
            ...editingRecord,
            ...payload,
            course_id: payload.course_id ?? editingRecord.course_id ?? editingRecord.Course_id ?? editingRecord.courseId ?? courseKey ?? courseId,
            topic_id: payload.topic_id
               ? payload.topic_id
               : (editingRecord.topic_id ?? editingRecord.Topic_id ?? editingRecord.topicId ?? null),
         };

         await updatePerformanceTask(mcptId, finalPayload, filesForUpload, mode);
         message.success(`${taskLabel} updated`);
         closeEditModal();
         await Promise.all([loadTasks(), loadDuplicatedTasks()]);
      } catch (error) {
         message.error(getErrorMessage(error, `Failed to update ${taskLabelLower}`));
      } finally {
         setSaving(false);
      }
   };

   const handleDelete = useCallback(async (record) => {
      try {
         const taskId = getTaskRecordId(record);
         if (!taskId) return;

         await deletePerformanceTask(taskId, mode);
         message.success(`${taskLabel} deleted`);
         await Promise.all([loadTasks(), loadDuplicatedTasks()]);
      } catch (error) {
         message.error(getErrorMessage(error, `Failed to delete ${taskLabelLower}`));
      }
   }, [getErrorMessage, loadDuplicatedTasks, loadTasks]);

   const handleDuplicate = useCallback(async (record) => {
      try {
         const taskId = getTaskMcptId(record) ?? getTaskRecordId(record);
         if (!taskId) {
            message.error(`Unable to duplicate this ${taskLabelLower}.`);
            return;
         }

         await duplicatePerformanceTask({
            mcptId: taskId,
            created_by: Number(userId),
            task_type: moduleConfig.taskType,
         }, mode);

         message.success(`${taskLabel} duplicated`);
         await loadDuplicatedTasks();
      } catch (error) {
         message.error(getErrorMessage(error, `Failed to duplicate ${taskLabelLower}`));
      }
   }, [getErrorMessage, loadDuplicatedTasks, userId]);

   const handleSearch = useCallback((selectedKeys, confirm, dataIndex) => {
      confirm();
      setSearchText(selectedKeys[0]);
      setSearchedColumn(dataIndex);
   }, []);

   const handleReset = useCallback((clearFilters) => {
      clearFilters();
      setSearchText('');
   }, []);

   const getColumnSearchProps = useCallback((dataIndex) => ({
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
         <div style={{ padding: 8 }}>
            <Input
               placeholder={`Search ${dataIndex}`}
               value={selectedKeys[0]}
               onChange={(event) => setSelectedKeys(event.target.value ? [event.target.value] : [])}
               onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
               style={{ width: 188, marginBottom: 8, display: 'block' }}
            />
            <Space>
               <Button
                  type="primary"
                  onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                  icon={<SearchOutlined />}
                  size="small"
                  style={{ width: 90 }}
               >
                  Search
               </Button>
               <Button
                  size="small"
                  style={{ width: 90 }}
                  onClick={() => {
                     handleReset(clearFilters);
                     confirm({ closeDropdown: false });
                     setSearchText(selectedKeys[0]);
                     setSearchedColumn(dataIndex);
                  }}
               >
                  Reset
               </Button>
            </Space>
         </div>
      ),
      filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#033D89' : undefined }} />,
      onFilter: (value, record) =>
         record[dataIndex]
            ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
            : '',
      render: (text) =>
         searchedColumn === dataIndex ? (
            <Highlighter
               highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
               searchWords={[searchText]}
               autoEscape
               textToHighlight={text ? text.toString() : ''}
            />
         ) : (
            text
         ),
   }), [handleReset, handleSearch, searchText, searchedColumn]);

   const openNewAssignmentDrawer = useCallback((record) => {
      const taskId = getTaskMcptId(record);
      if (!taskId) {
         message.error(`Unable to assign this ${taskLabelLower}.`);
         return;
      }

      setAssignmentTitle(getTaskTitle(record));
      setAssignmentOptions({
         id: '',
         mcpta_id: '',
         assignment_id: '',
         performance_task_id: taskId,
         performanceTaskId: taskId,
         mcpt_id: taskId,
         course_id: courseKey || courseId,
         assigned_by: Number(userId),
         level: '',
         submission_date: '',
         assigned_students: '',
         title: getTaskTitle(record),
      });
      setAssignmentPanelLoading(false);
      setAssignmentDrawerOpen(true);
   }, [courseId, courseKey, userId]);

   const openEditAssignmentDrawer = useCallback(async (record) => {
      const assignmentId = getAssignmentId(record);
      if (!assignmentId) {
         message.error('Unable to load assignment details.');
         return;
      }

      setAssignmentTitle(record.assignment_title || getTaskTitle(record));
      setAssignmentPanelLoading(true);
      setAssignmentDrawerOpen(true);

      try {
         const data = await getPerformanceTaskAssignment(assignmentId, mode);
         setAssignmentOptions(toAssignmentOptions({
            ...data,
            mcpt_id: data?.mcpt_id ?? record?.task_id ?? record?.mcpt_id ?? record?.performance_task?.mcpt_id ?? null,
         }, record.assignment_title));
      } catch (error) {
         message.error(getErrorMessage(error, 'Failed to load assignment details'));
         closeAssignmentDrawer();
      } finally {
         setAssignmentPanelLoading(false);
      }
   }, [closeAssignmentDrawer, getErrorMessage]);

   const handleAssignmentOptionsChange = useCallback((data) => {
      setAssignmentOptions({ ...data });
   }, []);

   const handleSaveAssignment = useCallback(async (data) => {
      try {
         const resolvedMcptId = data.mcpt_id ?? data.Mcpt_id ?? data.performance_task?.mcpt_id ?? data.performanceTask?.mcpt_id;
         if (!resolvedMcptId || String(resolvedMcptId).trim() === '') {
            message.error(`Unable to save assignment because the ${taskLabelLower} ID is missing. Please reopen the assignment panel and try again.`);
            return;
         }

         setAssignmentSaving(true);

         const assignmentId = getAssignmentId(data);
         const payload = {
            ...data,
            mcpta_id: data.mcpta_id ?? assignmentId ?? '',
            mcpt_id: resolvedMcptId,
            assigned_by: Number(data.assigned_by ?? userId),
         };

         if (assignmentId) {
            await updatePerformanceTaskAssignment(payload, mode);
            message.success(`${taskLabel} assignment updated`);
         } else {
            await createPerformanceTaskAssignment(payload, mode);
            message.success(`${taskLabel} assigned`);
         }

         closeAssignmentDrawer();
         await loadAssignmentsForTab();
      } catch (error) {
         message.error(getErrorMessage(error, `Failed to save ${taskLabelLower} assignment`));
      } finally {
         setAssignmentSaving(false);
      }
   }, [closeAssignmentDrawer, getErrorMessage, loadAssignmentsForTab, userId]);

   const openSubmissionsModal = useCallback((record) => {
      const nextTaskId = record?.task_id ?? getTaskMcptId(record) ?? getTaskRecordId(record);
      const nextLevelId = record?.level_value ?? record?.level ?? record?.level_id ?? record?.levelId ?? null;
      const nextAssignmentId = getAssignmentId(record);
      const nextAssignedBy = record?.assigned_by ?? record?.assignedBy ?? record?.assigned_by_id ?? record?.assignedById ?? null;
      const nextTitle = record?.assignment_title ?? getTaskTitle(record);
      const nextPlagiarismCheckerEnabled = isPlagiarismCheckerEnabled(
         record?.plagiarism_checker ?? record?.plagiarismChecker ?? record?.Plagiarism_checker
      );

      if (!nextTaskId || !nextLevelId || !nextAssignmentId) {
         message.error(`Unable to open submissions for this ${taskLabelLower} assignment.`);
         return;
      }

      setSubmissionTaskId(nextTaskId);
      setSubmissionLevelId(nextLevelId);
      setSubmissionAssignmentId(nextAssignmentId);
      setSubmissionAssignedBy(nextAssignedBy);
      setSubmissionTitle(nextTitle);
      setSubmissionPlagiarismCheckerEnabled(nextPlagiarismCheckerEnabled);
      setSubmissionsOpen(true);
   }, []);

   const masterColumns = useMemo(() => [
      {
         title: 'Title',
         dataIndex: 'title',
         key: 'title',
         sorter: (a, b) => (a.title || '').localeCompare(b.title || ''),
         ...getColumnSearchProps('title'),
         ellipsis: { showTitle: false },
         render: (title) => (
            <Tooltip placement="left" title={title}>
               <span style={{ cursor: 'default' }}>{title}</span>
            </Tooltip>
         ),
         width: '26%',
      },
      {
         title: 'Course',
         dataIndex: 'course_name',
         key: 'course_name',
         sorter: (a, b) => (a.course_name || '').localeCompare(b.course_name || ''),
         ...getColumnSearchProps('course_name'),
         ellipsis: { showTitle: false },
         render: (value) => (
            <Tooltip placement="left" title={value}>
               <span style={{ cursor: 'default' }}>{value || '-'}</span>
            </Tooltip>
         ),
         width: '18%',
      },
      {
         title: 'Topic',
         dataIndex: 'topic_title',
         key: 'topic_title',
         sorter: (a, b) => (a.topic_title || '').localeCompare(b.topic_title || ''),
         ...getColumnSearchProps('topic_title'),
         ellipsis: { showTitle: false },
         render: (value) => (
            <Tooltip placement="left" title={value}>
               <span style={{ cursor: 'default' }}>{value || '-'}</span>
            </Tooltip>
         ),
         width: '30%',
      },
      {
         title: 'Action',
         key: 'actions',
         render: (_, record) => (
            <Space size="small">                              
               {canEditPerformanceTask && (
                  <Tooltip title="Edit">
                     <EditOutlined
                        onClick={() => {
                           setEditingRecord({ ...record, source_tab: activeTab });
                           setEditOpen(true);
                        }}
                     />
                  </Tooltip>
               )}
               <Tooltip title="Preview">
                  <EyeOutlined
                     onClick={() => {
                        setPreviewRecord(record);
                        setPreviewOpen(true);
                     }}
                  />
               </Tooltip>
               {activeTab === TAB_KEYS.MASTER && (
                  <Popconfirm
                     title={`Create a copy of this item?`}
                     onConfirm={() => handleDuplicate(record)}
                     okText="Yes"
                     cancelText="No"
                  >
                     <Tooltip title="Duplicate">
                        <CopyOutlined />
                     </Tooltip>
                  </Popconfirm>
               )}
               <Tooltip title={`Assign ${taskLabel}`}>
                  <UsergroupAddOutlined onClick={() => openNewAssignmentDrawer(record)} />
               </Tooltip>
               {
                  canEditPerformanceTask && (
                     <Popconfirm
                        title={`Delete this item?`}
                        onConfirm={() => handleDelete(record)}
                        okText="Yes"
                        cancelText="No"
                     >
                        <Tooltip title="Delete">
                           <DeleteOutlined />
                        </Tooltip>
                     </Popconfirm>
                  )
               }               
            </Space>
         ),
         width: '12%',
      },
   ], [activeTab, canEditPerformanceTask, getColumnSearchProps, handleDelete, handleDuplicate, isSuperAdmin, openNewAssignmentDrawer]);

   const assignmentColumns = useMemo(() => [
      {
         title: 'Title',
         dataIndex: 'assignment_title',
         key: 'assignment_title',
         sorter: (a, b) => (a.assignment_title || '').localeCompare(b.assignment_title || ''),
         ...getColumnSearchProps('assignment_title'),
         ellipsis: { showTitle: false },
         render: (value) => (
            <Tooltip placement="left" title={value}>
               <span style={{ cursor: 'default' }}>{value}</span>
            </Tooltip>
         ),
         width: '24%',
      },
      {
         title: 'Grade Level',
         dataIndex: 'level_label',
         key: 'level_label',
         sorter: (a, b) => (a.level_label || '').localeCompare(b.level_label || ''),
         width: '10%',
      },
      {
         title: 'Assigned By',
         dataIndex: 'assigned_by_label',
         key: 'assigned_by_label',
         sorter: (a, b) => (a.assigned_by_label || '').localeCompare(b.assigned_by_label || ''),
         ...getColumnSearchProps('assigned_by_label'),
         ellipsis: { showTitle: false },
         render: (value) => (
            <Tooltip placement="left" title={value}>
               <span style={{ cursor: 'default' }}>{value}</span>
            </Tooltip>
         ),
         width: '14%',
      },
      {
         title: 'Assigned To',
         dataIndex: 'assigned_to_label',
         key: 'assigned_to_label',
         ellipsis: { showTitle: false },
         render: (value) => (
            <Tooltip placement="left" title={value}>
               <span style={{ cursor: 'default' }}>{value}</span>
            </Tooltip>
         ),
         width: '18%',
      },
      {
         title: 'Submission Date',
         dataIndex: 'submission_date_label',
         key: 'submission_date_label',
         sorter: (a, b) => (a.submission_date_label || '').localeCompare(b.submission_date_label || ''),
         render: (value) => formatDisplayDate(value),
         width: '13%',
      },
      {
         title: 'Date Assigned',
         dataIndex: 'date_assigned_label',
         key: 'date_assigned_label',
         sorter: (a, b) => (a.date_assigned_label || '').localeCompare(b.date_assigned_label || ''),
         render: (value) => formatDisplayDate(value),
         width: '13%',
      },
      {
         title: 'Action',
         key: 'actions',
         render: (_, record) => (
            <Space size="small">
               <Tooltip title="Edit Assignment">
                  <UsergroupAddOutlined onClick={() => openEditAssignmentDrawer(record)} />
               </Tooltip>
               <Tooltip title="Show Submissions">
                  <SolutionOutlined onClick={() => openSubmissionsModal(record)} />
               </Tooltip>
            </Space>
         ),
         width: '8%',
      },
   ], [getColumnSearchProps, openEditAssignmentDrawer, openSubmissionsModal]);

   const activeTaskList = useMemo(() => (
      activeTab === TAB_KEYS.MY_PERFORMANCE_TASK ? duplicatedTasks : tasks
   ), [activeTab, duplicatedTasks, tasks]);

   const activeAssignmentList = useMemo(() => {
      switch (activeTab) {
         case TAB_KEYS.CURRENT:
            return currentAssignments;
         case TAB_KEYS.UPCOMING:
            return upcomingAssignments;
         case TAB_KEYS.PAST:
            return pastAssignments;
         default:
            return [];
      }
   }, [activeTab, currentAssignments, pastAssignments, upcomingAssignments]);

   const getCardValue = useCallback((value) => {
      if (value === null || value === undefined || value === '') {
         return '—';
      }

      return value;
   }, []);

   const renderCardField = useCallback((label, value, { fullWidth = false } = {}) => (
      <div className={`brainee-module-record-field${fullWidth ? ' is-full' : ''}`}>
         <span className="brainee-module-record-label">{label}</span>
         <span className="brainee-module-record-value">{getCardValue(value)}</span>
      </div>
   ), [getCardValue]);

   const renderMasterCardActions = useCallback((record) => (
      <Space size="small">
         {canEditPerformanceTask && (
            <Tooltip title="Edit">
               <EditOutlined
                  onClick={() => {
                     setEditingRecord({ ...record, source_tab: activeTab });
                     setEditOpen(true);
                  }}
               />
            </Tooltip>
         )}
         <Tooltip title="Preview">
            <EyeOutlined
               onClick={() => {
                  setPreviewRecord(record);
                  setPreviewOpen(true);
               }}
            />
         </Tooltip>
         {activeTab === TAB_KEYS.MASTER && (
            <Popconfirm
               title={`Create a copy of this item?`}
               onConfirm={() => handleDuplicate(record)}
               okText="Yes"
               cancelText="No"
            >
               <Tooltip title="Duplicate">
                  <CopyOutlined />
               </Tooltip>
            </Popconfirm>
         )}
         <Tooltip title={`Assign ${taskLabel}`}>
            <UsergroupAddOutlined onClick={() => openNewAssignmentDrawer(record)} />
         </Tooltip>
         {canEditPerformanceTask && (
            <Popconfirm
               title={`Delete this item?`}
               onConfirm={() => handleDelete(record)}
               okText="Yes"
               cancelText="No"
            >
               <Tooltip title="Delete">
                  <DeleteOutlined />
               </Tooltip>
            </Popconfirm>
         )}
      </Space>
   ), [activeTab, canEditPerformanceTask, handleDelete, handleDuplicate, isSuperAdmin, openNewAssignmentDrawer]);

   const renderAssignmentCardActions = useCallback((record) => (
      <Space size="small">
         <Tooltip title="Edit Assignment">
            <UsergroupAddOutlined onClick={() => openEditAssignmentDrawer(record)} />
         </Tooltip>
         <Tooltip title="Show Submissions">
            <SolutionOutlined onClick={() => openSubmissionsModal(record)} />
         </Tooltip>
      </Space>
   ), [openEditAssignmentDrawer, openSubmissionsModal]);

   const renderMasterCardList = useCallback(() => (
      <div className="brainee-module-record-list-region">
         <div className="brainee-module-record-list">
            {activeTaskList.map((record, index) => (
               <article className="brainee-module-record-card" key={record.key || record.id || record.mcpt_id || `mc-performance-task-card-${index}`}>
                  <div className="brainee-module-record-header">
                     <div className="brainee-module-record-heading">
                        <div className="brainee-module-record-header-row">
                           <span className="brainee-module-record-kicker">{activeTab === TAB_KEYS.MY_PERFORMANCE_TASK ? myTaskLabel : taskLabel}</span>
                           {/* <span className="brainee-module-record-pill">{getCardValue(record.course_name)}</span> */}
                        </div>
                        <h3>{getCardValue(record.title)}</h3>
                        {/* <p>{getCardValue(record.topic_title)}</p> */}
                     </div>
                  </div>
                  <div className="brainee-module-record-meta">
                     {renderCardField('Course', record.course_name)}
                     {renderCardField('Topic', record.topic_title, { fullWidth: true })}
                  </div>
                  <div className="brainee-module-record-actions">
                     {renderMasterCardActions(record)}
                  </div>
               </article>
            ))}
         </div>
         <div className="brainee-module-record-list-footer">Total Records: {activeTaskList.length}</div>
      </div>
   ), [activeTab, activeTaskList, getCardValue, renderCardField, renderMasterCardActions]);

   const renderAssignmentCardList = useCallback(() => {
      const assignmentKicker = activeTab === TAB_KEYS.PAST ? 'Past Assignment' : 'Current Assignment';

      return (
         <div className="brainee-module-record-list-region">
            <div className="brainee-module-record-list">
               {activeAssignmentList.map((record, index) => (
                  <article className="brainee-module-record-card" key={record.key || record.id || record.mcpta_id || `mc-performance-task-assignment-card-${index}`}>
                     <div className="brainee-module-record-header">
                        <div className="brainee-module-record-heading">
                           <div className="brainee-module-record-header-row">
                              <span className="brainee-module-record-kicker">{assignmentKicker}</span>
                              <span className="brainee-module-record-pill">{formatDisplayDate(record.submission_date_label)}</span>
                           </div>
                           <h3>{getCardValue(record.assignment_title)}</h3>
                           <p>{getCardValue(record.assigned_to_label)}</p>
                        </div>
                     </div>
                     <div className="brainee-module-record-meta">
                        {renderCardField('Grade Level', record.level_label)}
                        {renderCardField('Assigned By', record.assigned_by_label)}
                        {renderCardField('Assigned To', record.assigned_to_label, { fullWidth: true })}
                        {renderCardField('Date Assigned', formatDisplayDate(record.date_assigned_label))}
                     </div>
                     <div className="brainee-module-record-actions">
                        {renderAssignmentCardActions(record)}
                     </div>
                  </article>
               ))}
            </div>
            <div className="brainee-module-record-list-footer">Total Records: {activeAssignmentList.length}</div>
         </div>
      );
   }, [activeAssignmentList, activeTab, getCardValue, renderAssignmentCardActions, renderCardField]);

   const renderTableSkeleton = () => (
      <div className="brainee-module-skeleton-table">
         <div className="brainee-module-skeleton-header">
            <Skeleton.Input active size="small" className="brainee-module-skeleton-title" />
            <Skeleton.Input active size="small" className="brainee-module-skeleton-chip" />
            <Skeleton.Input active size="small" className="brainee-module-skeleton-chip" />
            <Skeleton.Button active size="small" className="brainee-module-skeleton-action" />
         </div>
         {Array.from({ length: 6 }).map((_, index) => (
            <div className="brainee-module-skeleton-row" key={`performance-task-table-skeleton-${index}`}>
               <Skeleton.Input active size="small" className="brainee-module-skeleton-cell is-wide" />
               <Skeleton.Input active size="small" className="brainee-module-skeleton-cell" />
               <Skeleton.Input active size="small" className="brainee-module-skeleton-cell" />
               <Skeleton.Button active size="small" className="brainee-module-skeleton-action" />
            </div>
         ))}
      </div>
   );

   const activeLoading = [TAB_KEYS.MASTER, TAB_KEYS.MY_PERFORMANCE_TASK].includes(activeTab)
      ? (activeTab === TAB_KEYS.MY_PERFORMANCE_TASK ? loadingDuplicatedTasks : loadingTasks)
      : loadingAssignments[activeTab];

   const activeCount = [TAB_KEYS.MASTER, TAB_KEYS.MY_PERFORMANCE_TASK].includes(activeTab)
      ? activeTaskList.length
      : activeAssignmentList.length;

   const panelCopy = useMemo(() => {
      switch (activeTab) {
         case TAB_KEYS.MY_PERFORMANCE_TASK:
            return {
               title: myTaskLabel,
               description: `Review the ${taskLabelLower}s you duplicated for your own use.`,
            };
         case TAB_KEYS.CURRENT:
            return {
               title: 'Current Assignments',
               description: 'Assignments that are currently active for students.',
            };
         case TAB_KEYS.UPCOMING:
            return {
               title: 'Upcoming Assignments',
               description: 'Assignments scheduled ahead of time for this course.',
            };
         case TAB_KEYS.PAST:
            return {
               title: 'Past Assignments',
               description: 'Assignments whose submission windows have already passed.',
            };
         default:
            return {
               title: `${taskLabel} Records`,
               description: `Create, update, assign, and manage microcredential ${taskLabelLower}s for this course.`,
            };
      }
   }, [activeTab, myTaskLabel, taskLabel, taskLabelLower]);

   const tabItems = useMemo(() => ([
      {
         key: TAB_KEYS.MASTER,
         label: (
            <Tooltip title={`List of all created ${taskLabelLower}s`}>
               <span className="brainee-module-tab-label"><UnorderedListOutlined /> {taskLabel}</span>
            </Tooltip>
         ),
      },
      {
         key: TAB_KEYS.MY_PERFORMANCE_TASK,
         label: (
            <Tooltip title={`${taskLabelPlural} you duplicated`}>
               <span className="brainee-module-tab-label"><BookOutlined /> {myTaskLabel}</span>
            </Tooltip>
         ),
      },
      {
         key: TAB_KEYS.CURRENT,
         label: (
            <Tooltip title={`Current assigned ${taskLabelLower}s`}>
               <span className="brainee-module-tab-label"><ClockCircleOutlined /> Current Assignments</span>
            </Tooltip>
         ),
      },
      // {
      //    key: TAB_KEYS.UPCOMING,
      //    label: (
      //       <Tooltip title="Upcoming assigned performance tasks">
      //          <span className="brainee-module-tab-label"><CalendarOutlined /> Upcoming</span>
      //       </Tooltip>
      //    ),
      // },
      {
         key: TAB_KEYS.PAST,
         label: (
            <Tooltip title={`Past assigned ${taskLabelLower}s`}>
               <span className="brainee-module-tab-label"><HistoryOutlined /> Past Assignments</span>
            </Tooltip>
         ),
      },
   ]), [myTaskLabel, taskLabel, taskLabelLower, taskLabelPlural]);

   return (
      <>
         <div className="dashboard-area-all brainee-module-page">
            <div className="dashboard-item brainee-module-shell">
               <div className="brainee-module-breadcrumb">
                  <ResponsiveBreadcrumb
                     items={[
                        { key: 'home', node: <Link to="/home">Home</Link> },
                        { key: 'microcredentials', node: 'Brainee Microcredentials' },
                        { key: 'performance-tasks', node: <Link to={moduleConfig.teacherListRoute}>{taskLabelPlural}</Link> },
                        { key: 'course', node: courseName || 'Course' },
                     ]}
                  />
               </div>

               <section className="brainee-module-hero">
                  <div>
                     <span className="brainee-module-kicker">Brainee Microcredentials</span>
                     <h1>{courseName || `${taskLabel} List`}</h1>
                     <p>{`Create, assign, and organize ${taskLabelLower}s for this microcredential course.`}</p>
                     <div className="brainee-module-meta">
                        {/* <span><BookOutlined /> {activeLoading ? 'Loading records' : `${activeCount} records`}</span> */}
                        <span><FileTextOutlined /> {loadingTasks ? 'Loading' : `${tasks.length} ${taskLabelLower}s`}</span>
                        <span><BookOutlined /> {loadingDuplicatedTasks ? 'Loading' : `${duplicatedTasks.length} ${myTaskLabelPlural.toLowerCase()}`}</span>
                        <span><ClockCircleOutlined /> {(loadingTasks || loadingAssignments[TAB_KEYS.CURRENT]) ? 'Loading' : currentAssignments.length} assigned current</span>
                        <span><HistoryOutlined /> {(loadingTasks || loadingAssignments[TAB_KEYS.PAST]) ? 'Loading' : pastAssignments.length} assigned past</span>                        
                     </div>
                  </div>
                  <div className="brainee-module-actions">
                     {activeTab === TAB_KEYS.MASTER && isSuperAdmin && (
                        <Tooltip placement="bottom" title={`Create new ${taskLabel}`}>
                           <Button
                              className="brainee-module-create-btn"
                              type="primary"
                              size="large"
                              shape="round"
                              onClick={() => setCreateOpen(true)}
                           >
                              <PlusOutlined />Create {taskLabel}
                           </Button>
                        </Tooltip>
                     )}
                  </div>
               </section>

               <section className="brainee-module-panel">
                  <div className="brainee-module-panel-header">
                     <div>
                        <span className="brainee-module-kicker">Manage</span>
                        <h2>{panelCopy.title}</h2>
                        <p style={{ marginBottom: 0 }}>{panelCopy.description}</p>
                     </div>
                     <span className="brainee-module-count">{activeLoading ? '...' : activeCount}</span>
                  </div>

                  <Tabs
                     activeKey={activeTab}
                     className="brainee-module-tabs"
                     centered
                     items={tabItems}
                     onChange={setActiveTab}
                  />

                  {activeLoading
                     ? renderTableSkeleton()
                     : [TAB_KEYS.MASTER, TAB_KEYS.MY_PERFORMANCE_TASK].includes(activeTab)
                        ? (activeTaskList.length > 0
                           ? (
                              isCompactListView
                                 ? renderMasterCardList()
                                 : (
                                    // <div className="brainee-module-table-shell" style={{ height: 'calc(100vh - 31rem)' }}>
                                    <div className="brainee-module-table-shell">
                                       <Table
                                          className="brainee-module-table"
                                          rowKey="key"
                                          size="small"
                                          columns={masterColumns}
                                          dataSource={activeTaskList}
                                          pagination={false}
                                          scroll={{
                                             x: '1000px',
                                             y: 'calc(100vh - 35.2rem)',
                                          }}
                                          footer={() => <div className="brainee-module-table-footer">Total Records: {activeTaskList.length}</div>}
                                       />
                                    </div>
                                 )
                           )
                           : (
                              <div className="brainee-module-state">
                                 <Empty image="../../images/brainee_mascot.png" description={activeTab === TAB_KEYS.MY_PERFORMANCE_TASK ? `No duplicated ${taskLabelLower}s found` : `No ${taskLabelLower}s found`} />
                              </div>
                           ))
                        : (activeAssignmentList.length > 0
                           ? (
                              isCompactListView
                                 ? renderAssignmentCardList()
                                 : (
                                    // <div className="brainee-module-table-shell" style={{ height: 'calc(100vh - 31rem)' }}>
                                    <div className="brainee-module-table-shell">
                                       <Table
                                          className="brainee-module-table"
                                          rowKey="key"
                                          size="small"
                                          columns={assignmentColumns}
                                          dataSource={activeAssignmentList}
                                          pagination={false}
                                          scroll={{
                                             x: '1100px',
                                             y: 'calc(100vh - 35.2rem)',
                                          }}
                                          footer={() => <div className="brainee-module-table-footer">Total Records: {activeAssignmentList.length}</div>}
                                       />
                                    </div>
                                 )
                           )
                           : (
                              <div className="brainee-module-state">
                                 <Empty image="../../images/brainee_mascot.png" description="No assignments found" />
                              </div>
                           ))}
               </section>
            </div>
         </div>

         <Modal
            title={`Create ${taskLabel}`}
            // className="homework-assignment-modal"
            centered
            destroyOnClose
            onCancel={closeCreateModal}
            open={createOpen}
            width={1080}
            footer={[
               <Button key="cancel" onClick={closeCreateModal} disabled={saving}>Cancel</Button>,
               <Button key="save" type="primary" htmlType="submit" form="performance-task-create-form" loading={saving}>Save</Button>,
            ]}
         >
            <Spin spinning={saving} tip={`Creating ${taskLabelLower}, please wait...`}>
               <PerformanceTaskForm
                  formId="performance-task-create-form"
                  initialValues={null}
                  onSubmit={handleCreate}
                  loading={saving}
                  forcedCourseId={courseKey || courseId}

               />
            </Spin>
         </Modal>

         <Modal
            title={`Edit ${taskLabel}`}
            // className="homework-assignment-modal"
            centered
            destroyOnClose
            onCancel={closeEditModal}
            open={editOpen}
            width={1080}
            footer={[
               <Button key="cancel" onClick={closeEditModal} disabled={saving}>Cancel</Button>,
               <Button key="save" type="primary" htmlType="submit" form="performance-task-edit-form" loading={saving}>Save</Button>,
            ]}
         >
            <Spin spinning={saving} tip="Processing, please wait...">
               <PerformanceTaskForm
                  formId="performance-task-edit-form"
                  initialValues={editingRecord}
                  onSubmit={handleEdit}
                  loading={saving}
                  forcedCourseId={courseKey || courseId}
                  disableTopicSelection={editingRecord?.source_tab === TAB_KEYS.MY_PERFORMANCE_TASK}
               />
            </Spin>
         </Modal>

         <Modal
            title={`${taskLabel} Preview`}
            // className="homework-assignment-modal"
            centered
            destroyOnClose={true}
            open={previewOpen}
            width={1080}
            styles={{
               body: {
                  maxHeight: 'calc(100vh - 180px)',
                  overflowY: 'auto',
                  paddingRight: 8,
               },
            }}
            footer={[
               <Button key="close" shape="round" onClick={closePreviewModal}>Close</Button>,
            ]}
            onCancel={closePreviewModal}
         >
            <PerformanceTaskForm
               formId="performance-task-preview-form"
               initialValues={previewRecord}
               forcedCourseId={courseKey || courseId}
               readOnly={true}
            />
         </Modal>

         <Modal
            title={`${taskLabel} Submissions`}
            // className="homework-assignment-modal homework-submissions-modal"
            cancelText="Close"
            okButtonProps={{ style: { display: 'none' } }}
            centered
            destroyOnClose={true}
            open={submissionsOpen}
            width="90%"
            onCancel={closeSubmissionsModal}
         >
            <PerformanceTaskSubmissions
               homeworkTitle={submissionTitle}
               homeworkID={submissionTaskId}
               levelID={submissionLevelId}
               assignmentID={submissionAssignmentId}
               assignedBy={submissionAssignedBy}
               plagiarismCheckerEnabled={submissionPlagiarismCheckerEnabled}
               type="performance-task"
               mode={mode}
            />
         </Modal>

         <Drawer
            title={
               <div className="assessment-assign-drawer-title">
                  <span className="assessment-assign-drawer-icon"><UsergroupAddOutlined /></span>
                  <div>
                     <Text className="assessment-assign-kicker">Assignment</Text>
                     <h2>{assignmentTitle || `${taskLabel} Assignment`}</h2>
                  </div>
               </div>
            }
            placement="right"
            width={560}
            className="assessment-assign-drawer"
            closable={false}
            onClose={closeAssignmentDrawer}
            open={assignmentDrawerOpen}
            zIndex={1001}
            extra={
               <Space>
                  <Button className="assessment-assign-close-btn" type="default" shape="circle" icon={<CloseOutlined />} onClick={closeAssignmentDrawer} />
               </Space>
            }
         >
            <SimpleBarReact className="assessment-assign-scroll">
               <Spin spinning={assignmentPanelLoading || assignmentSaving} tip="Loading assignment panel...">
                  {assignmentOptions && (
                     <PerformanceTaskAssignTool
                        mode="drawer"
                        options={assignmentOptions}
                        parentCallback={handleAssignmentOptionsChange}
                        saveCallback={handleSaveAssignment}
                        manualSaveSlotId="performance-task-manual-save-slot"
                     />
                  )}
               </Spin>
            </SimpleBarReact>
            <div id="performance-task-manual-save-slot" className="assessment-assign-drawer-save-slot" />
         </Drawer>
      </>
   );
}
